import React, { useState } from "react";

const ValidatedInput = ({
  inputLabel,
  inputName,
  inputType,
  inputValue,
  inputRequired,
  onChange,
  errorMessage,
  phoneRequired = false,
  inputPlaceholder = inputValue,
  children,
}) => {
  const [touched, setTouched] = useState(false);
  const [valid, setValid] = useState(true);

  const currentDate = new Date().toISOString().split("T")[0];

  const validateInput = (value) => {
    let isFieldValid = true;

    switch (inputType) {
      case "email":
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        isFieldValid = emailPattern.test(value);
        break;

      case "phone":
        const phonePattern = /^(\(\d{3}\)\s?|\d{3}-)\d{3}-\d{4}$/;
        const justNumbers = /^\d{10}$/;
        if (justNumbers.test(value)) {
          isFieldValid = true;
        } else {
          isFieldValid = phonePattern.test(value);
        }
        break;

      case "date":
        isFieldValid = new Date(value) >= new Date(currentDate);
        break;

      default:
        isFieldValid = value !== "";
        break;
    }

    setValid(isFieldValid);
    return isFieldValid;
  };

  const handleInputChange = (event) => {
    let newValue = event.target.value;

    if (inputType === "phone" && /^\d{10}$/.test(newValue)) {
      newValue = `${newValue.slice(0, 3)}-${newValue.slice(
        3,
        6
      )}-${newValue.slice(6, 10)}`;
    } else if (inputType === "text") {
      newValue = newValue.trim();
    }

    setTouched(true);
    const isFieldValid = validateInput(newValue);
    onChange(inputName, newValue, isFieldValid);
  };

  const handleBlur = () => {
    setTouched(true);
    const isFieldValid = validateInput(inputValue);
    onChange(inputName, inputValue, isFieldValid);
  };

  const renderError = () => {
    if (!valid && touched) {
      let errorMsg = errorMessage || "Invalid input";

      if (!inputValue) {
        if (inputType === "phone" && !inputRequired) {
          return null; // Don't display any error if the phone isn't required and is empty.
        }
        errorMsg = "This field is required";
      } else {
        switch (inputType) {
          case "email":
            errorMsg = "Invalid Email Address";
            break;
          case "phone":
            errorMsg = "Invalid Phone Number";
            break;
          case "date":
            if (new Date(inputValue) < new Date(currentDate)) {
              errorMsg = "Date cannot be in the past";
            }
            break;
          default:
            errorMsg = "Invalid Input";
        }
      }

      return <div class="label-error">{errorMsg}</div>;
    }
    return null;
  };

  const commonProps = {
    id: inputName,
    name: inputName,
    value: inputValue,
    required: inputRequired,
    placeholder: inputPlaceholder,
    onChange: handleInputChange,
    onBlur: handleBlur,
    class: inputRequired && !valid && touched ? "input-error" : "input-regular",
  };

  switch (inputType) {
    case "checkbox":
      return (
        <div class="flex items-center mb-1">
          <input
            {...commonProps}
            type="checkbox"
            checked={inputValue}
            onChange={(event) => {
              handleInputChange(event);
              onChange(inputName, event.target.checked, true);
            }}
          />
          <label htmlFor={inputName} class="ml-2 text-sm">
            {inputLabel}
          </label>
        </div>
      );

    case "select":
      return (
        <div class="flex flex-col mb-1">
          <label
            htmlFor={inputName}
            class={
              inputRequired && !valid && touched
                ? "label-error"
                : "label-regular"
            }
          >
            {inputLabel}
          </label>
          <select {...commonProps}>{children}</select>
          <div class="min-h-[22px]">{renderError()}</div>
        </div>
      );

    case "text":
    case "email":
    case "phone":
      return (
        <div class="flex flex-col mb-1">
          <label
            htmlFor={inputName}
            class={
              inputRequired && !valid && touched
                ? "label-error"
                : "label-regular"
            }
          >
            {inputLabel}
          </label>
          <input {...commonProps} type="text" />
          <div class="min-h-[22px]">{renderError()}</div>
        </div>
      );

    case "date":
      return (
        <div class="flex flex-col mb-1">
          <label
            htmlFor={inputName}
            class={
              inputRequired && !valid && touched
                ? "label-error"
                : "label-regular"
            }
          >
            {inputLabel}
          </label>
          <input
            {...commonProps}
            type="date"
            placeholder={currentDate}
            min={currentDate}
            defaultValue={currentDate}
          />
          <div class="min-h-[22px]">{renderError()}</div>
        </div>
      );

    default:
      return <div>{children}</div>;
  }
};

export default ValidatedInput;
